import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { useAuth } from 'src/contexts/AuthContext'
import { useDocument } from 'react-firebase-hooks/firestore'
import { doc, Timestamp } from 'firebase/firestore'
import { firestore } from 'src/shims/firebase'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { createCustomerPortalSession, removeSeatFromSubscription } from '@batical/common'
import type { GroupSubscriber } from '@batical/common'
import { getPublicPageUrl } from 'src/utils/siteUrl'

type TeamRow = {
  id?: string
  mail?: string
  firstname?: string
  lastname?: string
  company?: string
  /** false = retiré du siège, données conservées */
  active?: boolean
  inactiveAt?: Timestamp | { seconds: number; nanoseconds: number }
  syncPending?: boolean
}

export function TeamPage() {
  const { user } = useAuth()
  const uid = user?.uid ?? ''
  const location = useLocation()
  const navigate = useNavigate()
  type TeamNavState = { pendingMembers?: GroupSubscriber[]; seatSummary?: string } | null
  const navState = location.state as TeamNavState
  const pendingFromNav = navState?.pendingMembers
  const [seatSummary, setSeatSummary] = useState<string | undefined>(() => navState?.seatSummary)

  const [snap, loading] = useDocument(doc(firestore, 'subscription', uid, 'groupSubscription', 'data'))
  const data = snap?.data()

  const subscribers: Array<{
    id?: string
    mail?: string
    firstname?: string
    lastname?: string
    company?: string
    active?: boolean
    inactiveAt?: Timestamp | { seconds: number; nanoseconds: number }
  }> = data?.subscribers ?? []
  const quantity = data?.quantity ?? 0
  const status = data?.status as string | undefined
  const hasSubscription = !!data?.stripeSubscriptionId

  const [removingMail, setRemovingMail] = useState<string>()
  const [confirmRemove, setConfirmRemove] = useState<{ open: boolean; row?: TeamRow }>({ open: false })
  const [errorSnackbar, setErrorSnackbar] = useState<string>()

  /** Emails still waiting for Stripe → Firestore webhook (full row with id). */
  const pendingNotYetInFirestore = useMemo(() => {
    if (!pendingFromNav?.length) return [] as GroupSubscriber[]
    const mails = new Set(subscribers.map((s) => (s.mail ?? '').toLowerCase().trim()).filter(Boolean))
    return pendingFromNav.filter((p) => {
      const m = (p.mail ?? '').toLowerCase().trim()
      return m && !mails.has(m)
    })
  }, [pendingFromNav, subscribers])

  const syncComplete =
    !pendingFromNav?.length || pendingNotYetInFirestore.length === 0

  useEffect(() => {
    if (!pendingFromNav?.length || !syncComplete) return
    navigate('/team', { replace: true, state: {} })
  }, [pendingFromNav, syncComplete, navigate])

  const displayRows: TeamRow[] = useMemo(() => {
    const rows: TeamRow[] = subscribers.map((s) => ({ ...s, syncPending: false }))
    for (const p of pendingNotYetInFirestore) {
      rows.push({
        mail: p.mail,
        firstname: p.firstname,
        lastname: p.lastname,
        company: p.company,
        syncPending: true
      })
    }
    rows.sort((a, b) => {
      const ai = a.active === false ? 1 : 0
      const bi = b.active === false ? 1 : 0
      if (ai !== bi) return ai - bi
      return (a.mail ?? '').localeCompare(b.mail ?? '')
    })
    return rows
  }, [subscribers, pendingNotYetInFirestore])

  const [filterQuery, setFilterQuery] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const filteredRows = useMemo(() => {
    const q = filterQuery.trim().toLowerCase()
    if (!q) return displayRows
    return displayRows.filter((row) => {
      const fullName = [row.firstname, row.lastname].filter(Boolean).join(' ').trim().toLowerCase()
      const haystack = [row.mail, row.firstname, row.lastname, row.company, fullName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [displayRows, filterQuery])

  useEffect(() => {
    setPage(0)
  }, [filterQuery])

  const pagedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  )

  const performRemoveMember = useCallback(async () => {
    const mail = confirmRemove.row?.mail
    if (!mail) return
    setConfirmRemove({ open: false })
    setRemovingMail(mail)
    try {
      await removeSeatFromSubscription(mail)
    } catch (err: any) {
      console.error('Remove seat error', err)
      setErrorSnackbar(err?.message ?? 'Erreur lors de la suppression.')
    } finally {
      setRemovingMail(undefined)
    }
  }, [confirmRemove.row?.mail])

  const memberDisplayName = (row: TeamRow) => {
    const n = [row.firstname, row.lastname].filter(Boolean).join(' ').trim()
    return n || row.mail || '—'
  }

  const formatInactiveAt = (v: TeamRow['inactiveAt']) => {
    if (!v) return null
    try {
      const d =
        v instanceof Timestamp
          ? v.toDate()
          : new Date(
              typeof (v as { seconds?: number }).seconds === 'number'
                ? (v as { seconds: number }).seconds * 1000
                : 0
            )
      if (Number.isNaN(d.getTime())) return null
      return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    } catch {
      return null
    }
  }

  const [portalLoading, setPortalLoading] = useState(false)
  const openPortal = useCallback(async () => {
    setPortalLoading(true)
    try {
      const { url } = await createCustomerPortalSession(getPublicPageUrl())
      window.location.href = url
    } catch (err: any) {
      console.error('Portal error', err)
      setPortalLoading(false)
    }
  }, [])

  return (
    <>
      <Dialog
        open={confirmRemove.open}
        onClose={() => !removingMail && setConfirmRemove({ open: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }
        }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#2c3e50', pb: 1 }}>
          Retirer ce membre ?
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ mb: 2 }}>
            <Typography component="div" variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {memberDisplayName(confirmRemove.row ?? {})}
            </Typography>
            {confirmRemove.row?.mail ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {confirmRemove.row.mail}
              </Typography>
            ) : null}
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Le siège est libéré tout de suite (facturation Stripe au prorata en général sur la prochaine facture). La
            personne n'a plus accès via cet abonnement, mais <strong>reste listée comme Inactif</strong> avec ses infos
            (nom, email, etc.) pour votre suivi.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmRemove({ open: false })}
            disabled={!!removingMail}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void performRemoveMember()}
            disabled={!!removingMail}
            disableElevation
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', px: 2.5 }}>
            {removingMail ? 'Suppression…' : 'Retirer du groupe'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={8000}
        onClose={() => setErrorSnackbar(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setErrorSnackbar(undefined)} sx={{ width: '100%', borderRadius: 2 }}>
          {errorSnackbar}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
          Mon équipe
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {hasSubscription && (
            <Button
              variant="outlined"
              onClick={openPortal}
              disabled={portalLoading}
              startIcon={<CreditCardIcon />}
              sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', px: 2.5 }}>
              {portalLoading ? 'Redirection…' : 'Gérer mon abonnement'}
            </Button>
          )}
          <Button
            component={Link}
            to="/team/add"
            variant="contained"
            disableElevation
            startIcon={<GroupAddIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              backgroundColor: '#4277b9',
              '&:hover': { backgroundColor: '#3666a3' }
            }}>
            Ajouter des membres
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <Typography variant="body1" sx={{ color: '#7f8c8d' }}>
          {quantity} siège{quantity > 1 ? 's' : ''} facturé{quantity > 1 ? 's' : ''} (Stripe) — la liste peut inclure des
          membres inactifs conservés à titre d'historique.
        </Typography>
        {status && (
          <Chip
            label={status}
            size="small"
            color={status === 'active' ? 'success' : status === 'canceled' ? 'error' : 'default'}
            variant="outlined"
            sx={{ fontWeight: 600, borderRadius: 1.5 }}
          />
        )}
      </Box>

      {seatSummary && (
        <Alert severity="success" onClose={() => setSeatSummary(undefined)} sx={{ mb: 2 }}>
          {seatSummary}
        </Alert>
      )}

      {!syncComplete && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Synchronisation Stripe en cours
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Les comptes membres apparaissent dès que le serveur confirme le paiement (généralement 10–60&nbsp;s). La ligne
            «&nbsp;En cours&nbsp;» disparaît quand tout est à jour.
          </Typography>
        </Alert>
      )}

      {loading ? (
        <Typography sx={{ color: '#7f8c8d' }}>Chargement…</Typography>
      ) : displayRows.length === 0 ? (
        <Paper
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed rgba(0,0,0,0.1)',
            backgroundColor: 'rgba(66,119,185,0.02)'
          }}>
          <Typography sx={{ color: '#7f8c8d', mb: 2, fontSize: '1.1rem' }}>
            Aucun membre pour le moment.
          </Typography>
          <Button
            component={Link}
            to="/team/add"
            variant="outlined"
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
            Ajouter des membres
          </Button>
        </Paper>
      ) : (
        <>
          <TextField
            size="small"
            fullWidth
            placeholder="Filtrer par nom, prénom, email ou entreprise…"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            sx={{ mb: 2, maxWidth: 480 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                </InputAdornment>
              )
            }}
          />

          {filteredRows.length === 0 ? (
            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary' }}>
                Aucun résultat pour «&nbsp;{filterQuery.trim()}&nbsp;». Modifiez ou effacez le filtre.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
              }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(66,119,185,0.04)' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }}>Prénom</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }}>Entreprise</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2c3e50', width: 60 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row, i) => (
                    <TableRow
                      key={row.id ?? row.mail ?? `p-${page}-${i}`}
                  sx={{
                    opacity: row.syncPending ? 0.92 : row.active === false ? 0.85 : 1,
                    backgroundColor: row.syncPending
                      ? 'rgba(66,119,185,0.06)'
                      : row.active === false
                        ? 'rgba(0,0,0,0.03)'
                        : undefined,
                    '&:hover': { backgroundColor: 'rgba(66,119,185,0.03)' }
                  }}>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {row.mail ?? '—'}
                      {row.syncPending && (
                        <Chip size="small" label="En cours" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                      )}
                      {row.active === false && (
                        <Tooltip
                          title={
                            formatInactiveAt(row.inactiveAt)
                              ? `Retiré le ${formatInactiveAt(row.inactiveAt)}`
                              : 'Membre retiré du siège'
                          }>
                          <Chip
                            size="small"
                            label="Inactif"
                            color="default"
                            variant="outlined"
                            sx={{ fontWeight: 600, borderColor: 'text.disabled', color: 'text.secondary' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.firstname ?? '—'}</TableCell>
                  <TableCell>{row.lastname ?? '—'}</TableCell>
                  <TableCell>{row.company ?? '—'}</TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                      {row.id && !row.syncPending && (
                        <Tooltip title="Voir le détail">
                          <IconButton
                            component={Link}
                            to={`/team/member/${row.id}`}
                            size="small"
                            color="primary"
                            sx={{ color: 'primary.main' }}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.mail && !row.syncPending && row.active !== false && (
                        <Tooltip title="Retirer du groupe">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={removingMail === row.mail}
                            onClick={() => setConfirmRemove({ open: true, row })}>
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      colSpan={5}
                      sx={{ border: 'none' }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      count={filteredRows.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(_, p) => setPage(p)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10))
                        setPage(0)
                      }}
                      labelRowsPerPage="Lignes par page"
                      labelDisplayedRows={({ from, to, count }) =>
                        count === 0 ? '0 résultat' : `${from}–${to} sur ${count}`
                      }
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </>
  )
}
